import { ResponseError } from 'types'
import { logNetworkRequest } from 'helpers/diagnostics'
import { APIError, ApiErrorCodes, HttpMethod } from 'helpers/apiError'

/**
 * Per-request context for {@link requestWithRetry}. The caller (helpers/api)
 * resolves these flags once and hands them in so this module stays free of the
 * heavy api.ts dependency graph and remains unit-testable in isolation.
 */
export interface MortaRequestContext {
  endpoint: string
  method: HttpMethod
  /** Public endpoints never trigger the forced sign-out on a 401. */
  isPublic: boolean
  /** Rate-ignored endpoints surface a 429 immediately instead of backing off. */
  isRateIgnored: boolean
  maxRetries: number
  /**
   * Invoked for a 401 (or `unauthorized` code) on a non-public endpoint. Expected
   * to throw (it redirects to sign-in); if it ever returns, the caller still
   * throws a typed 401 APIError as a fallback.
   */
  onUnauthorized: () => void
}

/** Injectable timing/randomness so tests run instantly and deterministically. */
export interface RetryDeps {
  sleep?: (ms: number) => Promise<void>
  rand?: () => number
}

// Messages shown to the user when a request gives up. The 5xx/incomplete ones still offer
// AI Support (shouldOfferSupport treats ApiErrorCodes.UNKNOWN / 5xx as faults).
export const TOO_MANY_REQUESTS_MESSAGE = "You're doing that too often. Wait a minute, then try again."
export const SERVER_ERROR_MESSAGE =
  "Morta couldn't complete that because of a problem on our side. Try again in a moment."
export const REQUEST_INCOMPLETE_MESSAGE = "Morta couldn't complete that. Check your internet connection and try again."

// 429 backoff tuning. Exponential growth, capped, with jitter so concurrent
// clients (multiple tabs / requests) do not all retry on the same tick and
// re-trip the limit in lockstep — the failure mode that turned a transient 429
// into a self-sustaining storm.
const RATE_LIMIT_BASE_MS = 500
const RATE_LIMIT_CAP_MS = 30_000
const RATE_LIMIT_JITTER = 0.5
// Delay between retries of transient (5xx / network) failures. Matches the
// legacy fixed 1s pause.
const TRANSIENT_RETRY_MS = 1000

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Delay before the next 429 retry: capped exponential backoff, raised to the
 * server's `X-RateLimit-Reset` hint when that is further out, plus bounded
 * jitter (0–50%).
 */
export function computeRateLimitDelayMs(
  resetEpochSec: number | null,
  attempt: number,
  nowMs: number,
  rand: () => number = Math.random
): number {
  let base = Math.min(RATE_LIMIT_BASE_MS * 2 ** (attempt - 1), RATE_LIMIT_CAP_MS)
  if (resetEpochSec && resetEpochSec > 0) {
    const untilResetMs = resetEpochSec * 1000 - nowMs
    if (untilResetMs > base) {
      base = Math.min(untilResetMs, RATE_LIMIT_CAP_MS)
    }
  }
  const jitter = base * RATE_LIMIT_JITTER * rand()
  return Math.round(base + jitter)
}

/**
 * Execute `send` with bounded retries.
 *
 * - 2xx → parsed body (204 → `{ status }`; other non-200 success merges `status`).
 * - 429 → backoff + retry up to `maxRetries` (none for rate-ignored endpoints), then
 *   throw a 429 APIError. This is the keystone fix: the legacy loop retried 429s forever.
 * - other 4xx → terminal, never retried; a 401 on a non-public endpoint calls `onUnauthorized`.
 * - 5xx / network error → retry up to `maxRetries`, then surface.
 */
export async function requestWithRetry(
  send: () => Promise<Response>,
  ctx: MortaRequestContext,
  deps: RetryDeps = {}
): Promise<unknown> {
  const sleep = deps.sleep ?? defaultSleep
  const rand = deps.rand ?? Math.random
  const { endpoint, method, isPublic, isRateIgnored, maxRetries, onUnauthorized } = ctx

  let attempt = 0
  while (attempt < maxRetries) {
    attempt++
    const requestStart = Date.now()

    let response: Response
    try {
      response = await send()
    } catch (networkError) {
      if (attempt >= maxRetries) throw networkError
      await sleep(TRANSIENT_RETRY_MS)
      continue
    }

    if (response.ok) {
      let content: unknown
      if (response.status === 204) {
        content = { status: response.status }
      } else {
        content = await response.json()
        if (response.status !== 200) {
          content = { ...(content as object), status: response.status }
        }
      }
      logNetworkRequest({
        timestamp: requestStart,
        method,
        endpoint,
        status: response.status,
        durationMs: Date.now() - requestStart
      })
      return content
    }

    // 429 Too Many Requests — bounded backoff retry, unless this endpoint opts out.
    if (response.status === 429) {
      if (!isRateIgnored && attempt < maxRetries) {
        const resetHeader = parseInt(response.headers.get('X-RateLimit-Reset') || '0', 10)
        const delay = computeRateLimitDelayMs(resetHeader > 0 ? resetHeader : null, attempt, Date.now(), rand)
        await sleep(delay)
        continue
      }
      logNetworkRequest({
        timestamp: requestStart,
        method,
        endpoint,
        status: 429,
        durationMs: Date.now() - requestStart,
        error: 'Too many requests'
      })
      let errorJson: Partial<ResponseError> = {}
      try {
        errorJson = (await response.json()) as ResponseError
      } catch {
        // The load balancer's rate limit (Cloud Armor) answers with its own
        // non-JSON body and no rate-limit headers; fall back to defaults.
      }
      throw new APIError(
        (errorJson.code as ApiErrorCodes) || ApiErrorCodes.TOO_MANY_REQUESTS,
        429,
        TOO_MANY_REQUESTS_MESSAGE,
        errorJson.detail?.errors,
        endpoint,
        method,
        errorJson.links
      )
    }

    // Other 4xx — terminal, never retried.
    if (response.status >= 400 && response.status < 500) {
      const errorJson = (await response.json()) as ResponseError
      if ((response.status === 401 || errorJson.code === ApiErrorCodes.UNAUTHORIZED) && !isPublic) {
        onUnauthorized() // throws / redirects to sign-in
      }
      logNetworkRequest({
        timestamp: requestStart,
        method,
        endpoint,
        status: response.status,
        durationMs: Date.now() - requestStart,
        error: errorJson.detail.message
      })
      throw new APIError(
        errorJson.code,
        response.status,
        errorJson.detail.message,
        errorJson.detail.errors,
        endpoint,
        method,
        errorJson.links,
        errorJson.needsSupport
      )
    }

    // 5xx / unknown — retry up to maxRetries, then surface.
    logNetworkRequest({
      timestamp: requestStart,
      method,
      endpoint,
      status: response.status,
      durationMs: Date.now() - requestStart,
      error: 'Unknown error'
    })
    if (attempt >= maxRetries) {
      throw new APIError(ApiErrorCodes.UNKNOWN, response.status, SERVER_ERROR_MESSAGE, undefined, endpoint, method)
    }
    await sleep(TRANSIENT_RETRY_MS)
  }

  // The loop always returns or throws above; this satisfies the type-checker.
  throw new APIError(ApiErrorCodes.UNKNOWN, 0, REQUEST_INCOMPLETE_MESSAGE, undefined, endpoint, method)
}
